import { Rocket, WarningCircle, Clock, HouseLine, ThumbsDown } from "@phosphor-icons/react";

type KpiFilterType = "interested" | "notFollowedUp" | "stale" | "awaitingInterested" | "notInterested";

interface KpiIndicatorBarProps {
  interestedCount: number;
  notFollowedUpCount: number;
  staleCount: number;
  awaitingInterestedCount: number;
  notInterestedCount: number;
  activeFilter: KpiFilterType | null;
  onFilterClick: (filter: KpiFilterType) => void;
  isLoading?: boolean;
}

interface KpiChipProps {
  icon: React.ReactNode;
  count: number;
  label: string;
  isActive: boolean;
  activeTextClass: string;
  activeBgClass: string;
  activeBorderClass: string;
  onClick: () => void;
}

const KpiChip = ({
  icon,
  count,
  label,
  isActive,
  activeTextClass,
  activeBgClass,
  activeBorderClass,
  onClick,
}: KpiChipProps) => {
  return (
    <button
      onClick={onClick}
      className={[
        "flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors",
        isActive
          ? `${activeBgClass} ${activeBorderClass} ${activeTextClass}`
          : "border-neutral-200 text-neutral-600 hover:border-neutral-300 hover:bg-neutral-50",
      ].join(" ")}
      type="button"
    >
      {icon}
      <span className="font-semibold">{count}</span>
      <span>{label}</span>
    </button>
  );
};

export const KpiIndicatorBar = ({
  interestedCount,
  notFollowedUpCount,
  staleCount,
  awaitingInterestedCount,
  notInterestedCount,
  activeFilter,
  onFilterClick,
  isLoading = false,
}: KpiIndicatorBarProps) => {
  const handleClick = (filter: KpiFilterType) => {
    onFilterClick(filter);
  };

  return (
    <div className={`flex flex-wrap items-center gap-1.5 py-1 ${isLoading ? "opacity-50" : ""}`}>
      <KpiChip
        icon={<Rocket size={14} weight="duotone" className={activeFilter === "interested" ? "text-orange-600" : "text-neutral-500"} />}
        count={interestedCount}
        label="geïnteresseerd"
        isActive={activeFilter === "interested"}
        activeTextClass="text-orange-600"
        activeBgClass="bg-orange-100"
        activeBorderClass="border-orange-300"
        onClick={() => handleClick("interested")}
      />
      <KpiChip
        icon={<WarningCircle size={14} weight="duotone" className={activeFilter === "notFollowedUp" ? "text-red-600" : "text-neutral-500"} />}
        count={notFollowedUpCount}
        label="niet opgevolgd"
        isActive={activeFilter === "notFollowedUp"}
        activeTextClass="text-red-600"
        activeBgClass="bg-red-100"
        activeBorderClass="border-red-300"
        onClick={() => handleClick("notFollowedUp")}
      />
      <KpiChip
        icon={<Clock size={14} weight="duotone" className={activeFilter === "stale" ? "text-amber-600" : "text-neutral-500"} />}
        count={staleCount}
        label="verouderd (> 20 dagen)"
        isActive={activeFilter === "stale"}
        activeTextClass="text-amber-600"
        activeBgClass="bg-amber-100"
        activeBorderClass="border-amber-300"
        onClick={() => handleClick("stale")}
      />
      <KpiChip
        icon={<HouseLine size={14} weight="duotone" className={activeFilter === "awaitingInterested" ? "text-purple-600" : "text-neutral-500"} />}
        count={awaitingInterestedCount}
        label="bezocht, nog geen kandidatuur"
        isActive={activeFilter === "awaitingInterested"}
        activeTextClass="text-purple-600"
        activeBgClass="bg-purple-100"
        activeBorderClass="border-purple-300"
        onClick={() => handleClick("awaitingInterested")}
      />
      <KpiChip
        icon={<ThumbsDown size={14} weight="duotone" className={activeFilter === "notInterested" ? "text-rose-600" : "text-neutral-500"} />}
        count={notInterestedCount}
        label="geen interesse"
        isActive={activeFilter === "notInterested"}
        activeTextClass="text-rose-600"
        activeBgClass="bg-rose-100"
        activeBorderClass="border-rose-300"
        onClick={() => handleClick("notInterested")}
      />
    </div>
  );
};
